/**
 * Privacy Policy Page (v0.10.0 - Issue #386)
 * 
 * Clean, honest privacy policy for all users.
 * Features clear TL;DR section and transparent data practices.
 */

import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import SectionGroup from '../components/SectionGroup';
import CalloutCard from '../components/CalloutCard';
import Divider from '../components/Divider';
import LastUpdated from '../components/LastUpdated';

export default function PrivacyPage() {
  return (
    <PageLayout>
      <PageHeader
        title="Privacy Policy"
        subtitle="Clear, honest answers about how we handle your data"
      />

      <CalloutCard title="TL;DR (Because We Know You Won't Read the Whole Thing)">
        <div className="space-y-4 text-text-secondary">
          <p>We don't sell your data.</p>
          <p>We don't spy on you.</p>
          <p>We don't hand your info to mysterious "partners."</p>
          <p>We barely collect anything in the first place.</p>
          
          <p>Your data is <strong className="text-text-primary">encrypted the second it leaves your fingers, stays that way the whole trip, and only turns back into words or pictures when it's safely on a screen you control.</strong></p>
          <p>(Except club chat — that's encrypted too, we just hold the key in case we need to remove the person spamming goat GIFs.)</p>
          
          <div>
            <p className="font-medium text-text-primary mb-2">The only people who ever see your stuff are:</p>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li><strong>You</strong>,</li>
              <li><strong>The clubs you choose to join</strong>, and</li>
              <li><strong>The unfortunate engineers keeping this thing running</strong><br/>(and even they can't see much — that's intentional).</li>
            </ol>
          </div>
          
          <div>
            <p className="font-medium text-text-primary mb-2">If you use GPS check-ins:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>We grab your location <strong>once</strong>,</li>
              <li>Compare it <strong>in memory</strong> to the event location,</li>
              <li>Save only <strong>yes/no</strong> to the database,</li>
              <li>And the coordinates disappear instantly.</li>
            </ul>
          </div>
          
          <p>That's it.<br/>No drama. No shady surprises. No "gotchas."</p>
          <p className="font-medium text-text-primary">
            Just a simple promise: <strong>your data isn't our business model.</strong>
          </p>
          <p>Carry on.</p>
        </div>
      </CalloutCard>

      <LastUpdated date="December 2025" />

      <Section>
        <p className="text-text-secondary leading-relaxed">
          Your privacy matters. We built ORO from day one with a simple rule: <strong className="text-text-primary">your data belongs to you, not us.</strong> This page explains what we collect, how we use it, and how we protect it.
        </p>
      </Section>

      <Divider />

      <SectionGroup>
        <Section 
          title="1. What We Collect"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>We only collect the information needed for ORO to work:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your email address (for login links)</li>
              <li>Your profile details (name, photo if you choose to upload one)</li>
              <li>Club membership and RSVP activity</li>
              <li>Messages you post in clubs (if your club uses chat)</li>
              <li>Basic telemetry that helps us keep the app stable and detect abuse</li>
            </ul>
            <p>
              We do <strong className="text-text-primary">not</strong> track your location, sell behavioral data, or run ads.
            </p>
          </div>
        </Section>

        <Section 
          title="2. What We Never Do"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>Some things are absolute:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-text-primary">We never sell or rent your personal information.</strong></li>
              <li><strong className="text-text-primary">We never share your data with advertisers.</strong></li>
              <li><strong className="text-text-primary">We never allow third parties to harvest your information for profiling.</strong></li>
            </ul>
            <p>Your data will never be a revenue stream. Period.</p>
          </div>
        </Section>

        <Section 
          title="3. How Your Data Is Protected"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              Your information is encrypted <strong className="text-text-primary">in transit</strong> from the moment it leaves your device and <strong className="text-text-primary">in storage</strong> once it reaches our infrastructure. When data returns to you, it's encrypted again until it hits your screen.
            </p>
            <div>
              <p>We use:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>End-to-end TLS</li>
                <li>Cloudflare's hardened Workers and database layers</li>
                <li>Strict access controls</li>
                <li>Industry-standard hashing for authentication tokens</li>
                <li>Audit logging on sensitive actions</li>
              </ul>
            </div>
            <p>
              We design systems so the safest assumption is <strong className="text-text-primary">"only you should see your data."</strong>
            </p>
          </div>
        </Section>
      </SectionGroup>

      <Divider />

      <SectionGroup>
        <Section 
          title="4. How We Use Your Data"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>We use your information only to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Help you access your account</li>
              <li>Join and participate in clubs</li>
              <li>Create and RSVP to events</li>
              <li>Send notifications you've opted into</li>
              <li>Keep the platform secure and detect misuse</li>
            </ul>
            <p>We don't repurpose your data for anything outside of running ORO.</p>
          </div>
        </Section>

        <Section 
          title="5. When We Share Data"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              We share the minimum needed with trusted infrastructure providers so the app can function. Examples: Cloudflare (hosting), email service for login links.
            </p>
            <p>
              These providers <strong className="text-text-primary">cannot</strong> use your data for their own purposes.
            </p>
            <p>
              We do <strong className="text-text-primary">not</strong> sell or broker your data — ever.
            </p>
          </div>
        </Section>

        <Section 
          title="6. Club Visibility"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>ORO lets each club choose whether it is:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Public (anyone can view, joining still requires invitation unless configured otherwise)</li>
              <li>Private (visible only to members)</li>
              <li>Hidden (invitation-only and not discoverable)</li>
            </ul>
            <p>Club owners manage visibility, and you can leave any club at any time.</p>
          </div>
        </Section>
      </SectionGroup>

      <Divider />

      <SectionGroup>
        <Section 
          title="7. Data You Delete"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>
              When you remove something (a message, a photo, your profile), we delete it from active systems. Backups may retain fragments for a short period, but they age out automatically.
            </p>
            <p>If you delete your account, we remove your personal data from our systems.</p>
          </div>
        </Section>

        <Section 
          title="8. Children's Privacy"
          headingLevel="h3"
          showDivider={false}
        >
          <p className="text-text-secondary leading-relaxed">
            ORO is not designed for children under 13. If we learn an underage user created an account, we remove it.
          </p>
        </Section>

        <Section 
          title="9. Changes to This Policy"
          headingLevel="h3"
          showDivider={false}
        >
          <p className="text-text-secondary leading-relaxed">
            If we ever make a meaningful change to how we handle privacy, we'll tell you clearly in the app — not buried in fine print.
          </p>
        </Section>

        <Section 
          title="10. Contact"
          headingLevel="h3"
          showDivider={false}
        >
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>Questions or concerns?</p>
            <p>
              <a 
                href="mailto:privacy@oro.fit" 
                className="text-accent hover:text-accent-light transition-colors font-medium"
              >
                privacy@oro.fit
              </a>
            </p>
            <p>We take privacy seriously, and we'll respond.</p>
          </div>
        </Section>
      </SectionGroup>
    </PageLayout>
  );
}